import uniqueId from 'lodash/uniqueId';

import { UseSelectedCutoutLaborResponse } from '~/api/easy-estimator-api/cutout-labor-api/hooks/useCutoutLabor';
import {
  BACKSPLASH_ID,
  LIST_ITEM,
  UNIT_MEASURE,
  WATERFALL_ID,
} from '~/core/constants';
import { isRadiusOut } from '~/core/enums';
import type {
  ApplianceModel,
  ColorModel,
  CornerRadiusModel,
  CutoutLaborModel,
  DiagonalCornerModel,
  DrawingAppliance,
  DrawingEdge,
  DrawingShape,
  PricedProduct,
  ProductEstimateQuery,
  ProductQty,
} from '~/core/models';
import { ApplianceType, getCornerRadiusFromSize } from '~/core/models';
import { ApplianceRecord } from '~/core/types/appliances';
import {
  hasRequiredProps,
  incById,
  isActualProduct,
  isCustomerSuppliedAppliance,
} from '~/core/utils';
import type { CookingAppliancesState } from '~/redux/slices/cookingAppliance';
import type { CutoutState } from '~/redux/slices/cutout';
import type { TearOutState } from '~/redux/slices/tearOut';
import type { FaucetContextValue } from '~contexts';

/**
 * This maps a selectedSinks object to an array of {productId, quantity} (i.e. pricedProducts) records
 * @param selectedSinks
 */
export const mapSinkRecordToPricedSinks = (
  selectedSinks: Record<ApplianceModel['productId'], number>
) =>
  Object.entries(selectedSinks).map<ProductQty>(([id, quantity]) => ({
    productId: +id,
    quantity,
  }));

export const getPricedSinksCutouts = (
  pricedSinks: ProductQty[],
  sinksProducts: ApplianceModel[],
  selectSinkCutout: UseSelectedCutoutLaborResponse['selectSinkCutout'],
  productLine: string | null | undefined
) =>
  pricedSinks.reduce<ProductQty[]>((acc, pricedSink) => {
    const sinkAppliance = sinksProducts.find(
      (sink) => sink.productId === pricedSink.productId
    );

    if (sinkAppliance?.applianceType === ApplianceType.SINK) {
      const cutoutLaborProduct = selectSinkCutout({
        materialType: productLine,
        sink: sinkAppliance,
        _source: 'pricedProducts',
      });

      if (cutoutLaborProduct) {
        return [
          ...acc,
          {
            productId: cutoutLaborProduct.productId,
            quantity: pricedSink.quantity,
          },
        ];
      }
    }

    return acc;
  }, []);

export const getPricedTearOuts = (
  selectedTearOuts: TearOutState['selectedTearOuts']
) =>
  Object.entries(selectedTearOuts)
    .filter(([, qty]) => qty > 0)
    .map<ProductQty>(([productId, qty]) => ({
      productId: Number(productId),
      quantity: qty,
    }));

export const getPricedCutouts = (
  selectedCutouts: CutoutState['customCutouts']['selected']
) =>
  Object.entries(selectedCutouts)
    .filter(([, qty]) => (qty.onCard ?? 0) > 0 || (qty.onShape ?? 0) > 0)
    .map<ProductQty>(([productId, qty]) => {
      const totalQuantity = (qty.onCard ?? 0) + (qty.onShape ?? 0);

      return {
        productId: Number(productId),
        quantity: totalQuantity,
      };
    })
    .filter((item) => item.quantity > 0);

export const getPricedApplianceCutouts = (
  applianceCutouts: CutoutState['applianceCutouts']['selected']
) =>
  Object.entries(applianceCutouts)
    .flatMap(([cutoutId, applianceRecord]) =>
      Object.entries(applianceRecord).map(([_applianceId, quantity]) => ({
        productId: Number(cutoutId),
        quantity,
      }))
    )
    .filter((item) => item.quantity > 0);

export const getPricedFaucets = (
  selectedFaucets: FaucetContextValue['selectedFaucets']
) =>
  Object.entries(selectedFaucets)
    .filter(([, faucet]) => faucet.isSelected && faucet.quantity > 0)
    .map<ProductQty>(([productId, faucet]) => ({
      productId: Number(productId),
      quantity: faucet.quantity,
    }));

/**
 * Maps all _actual_ cooking appliances from the selectedCookingAppliances to pricedProducts with quantities
 * Customer supplied appliances are removed
 * @param selectedCooktops
 * @param selectedRanges
 * @param allCookingAppliances
 */
export const getPricedCookingAppliances = (
  selectedCooktops: CookingAppliancesState['selectedCooktopApplianceProduct']['selected'],
  selectedRanges: CookingAppliancesState['selectedRangeApplianceProduct']['selected'],
  allCookingAppliances: ApplianceModel[] = []
) => {
  const applianceEntries = [selectedCooktops, selectedRanges].flatMap(
    (cooktopAppliance) => Object.entries(cooktopAppliance)
  );

  const appliancesWithQuantity = applianceEntries.filter(
    ([, qty]) => (qty.onCard ?? 0) > 0 || (qty.onShape ?? 0) > 0
  );

  const productQuantities = appliancesWithQuantity.map<ProductQty>(
    ([productId, qty]) => ({
      productId: Number(productId),
      quantity: (qty.onCard ?? 0) + (qty.onShape ?? 0),
    })
  );

  return productQuantities.filter(
    ({ productId }) =>
      !isCustomerSuppliedAppliance(
        allCookingAppliances.find(
          (appliance) => appliance.productId === productId
        )
      )
  );
};

/**
 * Calculate the total quantity of cooktopCutouts based on the selectedCooktops
 * @param selectedCooktops all of the cooktops quantities, including in-shape, just added, real, or customer supplied
 * @param cooktopCutout the cutout to use for each cooktop being added
 */
export const getPricedCooktopCutouts = (
  selectedCooktops: ApplianceRecord,
  cooktopCutout: CutoutLaborModel | undefined
) => {
  const totalCooktopCutoutsNeeded = Object.values(selectedCooktops)
    .flatMap((something) => Object.values(something))
    .reduce((acc, val) => acc + val, 0);

  if (totalCooktopCutoutsNeeded && cooktopCutout) {
    return [
      {
        productId: cooktopCutout?.productId,
        quantity: totalCooktopCutoutsNeeded,
      },
    ];
  }

  return [];
};

export const getCornerProducts = (
  drawingShape: DrawingShape,
  insideDiagonalCorner?: DiagonalCornerModel
) =>
  Object.values(drawingShape.cornerMap).reduce<Record<number, number>>(
    (corners, corner) => {
      const id =
        corner.hasPredefinedExtraEdging &&
        Boolean(corner.diagonalLength) &&
        (corner.angle ?? 0) < 0
          ? insideDiagonalCorner?.productId
          : corner.productId;

      return incById(corners, corner.isVisibleInCanvas && id);
    },
    {}
  );

export const getEdgeProducts = (
  drawingShape: DrawingShape,
  cornerRadii: CornerRadiusModel[]
): Record<number, number> =>
  Object.values(drawingShape.drawingEdges)
    .filter(
      (edge) =>
        isEdgeWithValidRadius(edge) && hasRequiredProps(edge, ['realRadius'])
    )
    .map((edge) =>
      edge.realRadius
        ? getCornerRadiusFromSize(edge.realRadius, cornerRadii)
        : undefined
    )
    .filter(
      (cornerRadius): cornerRadius is CornerRadiusModel =>
        cornerRadius !== undefined && isActualProduct(cornerRadius.productId)
    )
    .map((cornerRadius) => cornerRadius.productId)
    .reduce<Record<number, number>>(incById, {});

const isEdgeWithValidRadius = (edge: DrawingEdge): boolean =>
  isRadiusOut(edge) && edge.isVisibleInCanvas && edge.realRadius !== undefined;

/**
 * Add any other appliances that haven't already been accounted for
 * @param productEstimateQuery the current productEstimateQuery, which will be mutated
 */
export const addOtherAppliances =
  (productEstimateQuery: ProductEstimateQuery) =>
  (appliance: DrawingAppliance) => {
    const { applianceType, productId } = appliance.applianceModel;

    if (
      applianceType === ApplianceType.DISHWASHER ||
      applianceType === ApplianceType.REFRIGERATOR
    ) {
      productEstimateQuery.products.push({ productId, quantity: 1 });
    }
  };

export const accumulateDuplicateCornerRadii = (
  estimateQueryProducts: ProductQty[]
) => {
  const products = [...estimateQueryProducts];

  return products
    .reduce((accumulator: ProductQty[], currentProductQty: ProductQty) => {
      const acc = [...accumulator];
      const duplicateProd = acc.findIndex(
        ({ productId }) =>
          !currentProductQty.lineItemId &&
          productId === currentProductQty.productId
      );

      if (duplicateProd !== -1) {
        acc[duplicateProd] = {
          ...acc[duplicateProd],
          quantity: acc[duplicateProd].quantity + currentProductQty.quantity,
        };
      } else {
        acc.push(currentProductQty);
      }

      return acc;
    }, [])
    .filter((product) => product.quantity > 0);
};

export const getMappedPricedProduct = (
  product: PricedProduct,
  selectedColor: ColorModel,
  backsplashTitle: string,
  waterfallTitle: string
) => {
  // rename backsplashes and waterfalls
  // so they're not the same as the main countertop
  const name = {
    [BACKSPLASH_ID]: backsplashTitle,
    [WATERFALL_ID]: waterfallTitle,
  }[product.lineItemId ?? -1];
  const nameDesc = name
    ? { name, consumerDesc: name, abbrevDesc: name }
    : undefined;
  // This overrides the unit of measure for any of the SLAB products
  // because they're "ea" in the database and need to be "sq ft" here.
  const unitOfMeasure =
    product.productId === selectedColor?.productId
      ? UNIT_MEASURE.MATERIALS
      : product.unitOfMeasure;

  return {
    ...product,
    listKey: `${LIST_ITEM}_${uniqueId()}`,
    unitOfMeasure,
    ...nameDesc,
  };
};

export const getNumberOfChipMinimizersInDrawingShapes = (
  drawingShapes: DrawingShape[]
): number =>
  drawingShapes.reduce((acc, next) => {
    next.appliances.forEach((appliance) => {
      if (appliance.hasChipMinimizer) {
        acc += 1;
      }
    });

    return acc;
  }, 0);
